/**
 * scene-background-library (機構 M15) Task 3.3:
 * visual_novel モード時のシーン背景画像を取得・検索する React Query hook。
 *
 * 設計方針:
 * - enabled=false (= text_scroll モード) のとき fetch を行わない (req 4.5)
 * - staleTime: Infinity — プレイセッション中にライブラリは変化しない前提 (req 4.3/4.4)
 * - getImageUrl は Map<string, string> で O(1) 検索 (req 4.1/4.2)
 * - null/undefined 入力・一致なしは null を返す (req 4.2)
 *
 * 設計詳細は `.kiro/specs/scene-background-library/design.md` §useSceneBackground を参照。
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { components } from "../../api/schema";
import {
  listBackgrounds,
  type SceneBackgroundResponse,
} from "../../api/scene-background";

export interface UseSceneBackgroundResult {
  /**
   * versionedではnode key、それ以外ではlabel → imageUrlのO(1)検索。
   * - key が null/undefined のとき null を返す
   * - 一致する背景が存在しないとき null を返す
   */
  getImageUrl: (key: string | null | undefined) => string | null;
  /** fetch 中 boolean。enabled=false のとき常に false。 */
  isLoading: boolean;
}

/**
 * visual_novel モード時の背景画像ライブラリを取得し、ラベル→URL 変換を提供する hook。
 *
 * @param scenarioId - 背景を取得するシナリオ ID。undefined のとき fetch しない。
 * @param enabled - displayMode === "visual_novel" のとき true (req 4.5)
 * @param baseUrl - API ベース URL (例: "/api/v1" or "http://localhost:3000/api/v1")
 * @param visualAssets - 同版contextの画像map。versionedはlibraryを利用しない。
 */
export function useSceneBackground(
  scenarioId: string | undefined,
  enabled: boolean,
  baseUrl: string,
  visualAssets?: components["schemas"]["VisualAssets"],
): UseSceneBackgroundResult {
  const versionedAssets =
    visualAssets?.mode === "versioned" ? visualAssets : undefined;
  const queryEnabled =
    enabled && !versionedAssets && scenarioId !== undefined && scenarioId !== "";

  const { data, isLoading } = useQuery<SceneBackgroundResponse[]>({
    queryKey: ["scene-backgrounds", scenarioId],
    enabled: queryEnabled,
    staleTime: Infinity,
    queryFn: async () => {
      // enabled: false / scenarioId falsy の場合は queryFn は呼ばれない。
      // 型を狭めるための保険的なガード。
      if (!scenarioId) {
        return [];
      }
      return listBackgrounds(baseUrl, scenarioId);
    },
  });

  // 固定版では古いlibrary cacheを検索対象へ取り込まない。
  const imageUrlMap = useMemo<Map<string, string>>(() => {
    if (versionedAssets || !data) return new Map();
    const map = new Map<string, string>();
    for (const bg of data) {
      map.set(bg.label, bg.imageUrl);
      for (const alias of bg.aliases ?? []) {
        map.set(alias, bg.imageUrl);
      }
    }
    return map;
  }, [data, versionedAssets]);

  const getImageUrl = useMemo(
    () =>
      (key: string | null | undefined): string | null => {
        if (key === null || key === undefined) return null;
        // query無効時にも古いlibrary cacheは残るため、固定版は独立して検索する。
        if (versionedAssets) {
          const backgrounds = versionedAssets.backgroundsByNodeKey;
          return Object.hasOwn(backgrounds, key)
            ? backgrounds[key]?.url || null
            : null;
        }
        return imageUrlMap.get(key) ?? null;
      },
    [imageUrlMap, versionedAssets],
  );

  return {
    getImageUrl,
    // enabled=false のとき React Query は isFetching=false / isPending=true だが
    // isLoading (= isPending && isFetching) は false になる。
    isLoading: queryEnabled ? isLoading : false,
  };
}
